import { useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    TextField,
    Card,
    CardContent,
    Typography,
    Divider,
    useTheme,
    Grid
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/system';
import {
    getIndividualLab,
    createLab,
    editLabApi,
    deleteLab
} from '../../../../API/Lab';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialogue from '../Components/Dialogue';
import {
    resetCurrentScreenForm,
    setCurrentScreenId,
    setCurrentScreenName,
    setCurrentScreenState
} from '../Slices/currentScreen';

const Lab = () => {
    const loader = useSelector((state) => state?.currentScreen?.isLoading);
    const [labName, setLabName] = useState('');
    const [labUrl, setLabUrl] = useState('');
    const [open, setOpen] = useState(false);

    const [errors, setErrors] = useState({
        labName: { isError: false, message: '' },
        labUrl: { isError: false, message: '' }
    });

    const isEditMode = useSelector((state) => state?.currentScreen?.editable);
    const shouldResetForm = useSelector(
        (state) => state?.currentScreen?.resetForm
    );
    const sectionId = useSelector(
        (state) => state?.currentCourse?.currentSectionId
    );
    const labId = useSelector((state) => state?.currentScreen?.id);
    const dispatch = useDispatch();
    const theme = useTheme();

    const handleLabName = (e) => {
        setLabName(e.target.value);
        if (e.target.value.trim() === '') {
            setErrors((prev) => ({
                ...prev,
                labName: { isError: true, message: 'Lab Name is required' }
            }));
        } else {
            setErrors((prev) => ({
                ...prev,
                labName: { isError: false, message: '' }
            }));
        }
    };

    const handleLabUrl = (e) => {
        setLabUrl(e.target.value);
        if (e.target.value.trim() === '') {
            setErrors((prev) => ({
                ...prev,
                labUrl: { isError: true, message: 'Lab URL is required' }
            }));
        } else {
            setErrors((prev) => ({
                ...prev,
                labUrl: { isError: false, message: '' }
            }));
        }
    };

    const handleDeleteLab = async () => {
        await deleteLab(labId, sectionId);
        toast.success('Lab deleted successfully');
        dispatch(resetCurrentScreenForm(true));
        setOpen(false);
    };

    const submitLab = async (e) => {
        let hasError = false;

        if (labName.trim() === '') {
            setErrors((prev) => ({
                ...prev,
                labName: { isError: true, message: 'Lab Name is required' }
            }));
            hasError = true;
        }
        if (labUrl.trim() === '') {
            setErrors((prev) => ({
                ...prev,
                labUrl: { isError: true, message: 'Lab URL is required' }
            }));
            hasError = true;
        }

        if (!hasError) {
            try {
                if (isEditMode) {
                    await editLabApi(labId, {
                        labName: labName.trim(),
                        url: labUrl.trim()
                    });
                    toast.success('Lab updated successfully');
                    dispatch(resetCurrentScreenForm(false));
                } else {
                    const response = await createLab(sectionId, {
                        labName: labName.trim(),
                        url: labUrl.trim()
                    });
                    toast.success('Lab created successfully');
                    const createdLabId = response?.data?._id || response?._id;
                    if (createdLabId) {
                        dispatch(setCurrentScreenName('lab'));
                        dispatch(setCurrentScreenId(createdLabId));
                        dispatch(setCurrentScreenState(true));
                    } else {
                        dispatch(resetCurrentScreenForm(true));
                    }
                }
            } catch (err) {
                toast.error('Failed to save Lab');
            }
        }
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    useEffect(() => {
        if (isEditMode && labId) {
            getIndividualLab(labId).then((response) => {
                if (response?.data) {
                    setLabName(response.data.labName || '');
                    setLabUrl(response.data.url || '');
                }
            });
        }
    }, [isEditMode, labId]);

    useEffect(() => {
        if (shouldResetForm) {
            setLabName('');
            setLabUrl('');
        }
    }, [shouldResetForm]);

    return (
        <>
            {loader ? (
                <Box
                    textAlign="center"
                    height="40vh"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Box>
                    <Card
                        elevation={3}
                        sx={{
                            borderRadius: '16px',
                            border: `1px solid ${theme.palette.divider}`,
                            overflow: 'hidden',
                            mb: 2
                        }}
                    >
                        {/* Header Details */}
                        <Box
                            sx={{
                                p: 3,
                                backgroundColor: theme.palette.primary.main,
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 700, mb: 0.5 }}
                                >
                                    {isEditMode
                                        ? 'Lab Settings'
                                        : 'Create New Lab'}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ opacity: 0.8 }}
                                >
                                    {isEditMode
                                        ? 'Update lab details or delete this lab from the section.'
                                        : 'Add a new virtual lab environment for practical assignments.'}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider />

                        <CardContent sx={{ p: 4 }}>
                            <Box display="flex" flexDirection="column" gap={3}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.secondary'
                                    }}
                                >
                                    Virtual Lab Details
                                </Typography>

                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Lab Name"
                                            name="labName"
                                            fullWidth
                                            value={labName}
                                            variant="outlined"
                                            onChange={handleLabName}
                                            error={Boolean(
                                                errors.labName.isError
                                            )}
                                            helperText={
                                                errors.labName.isError &&
                                                errors.labName.message
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Lab URL"
                                            name="labUrl"
                                            fullWidth
                                            value={labUrl}
                                            variant="outlined"
                                            onChange={handleLabUrl}
                                            error={Boolean(
                                                errors.labUrl.isError
                                            )}
                                            helperText={
                                                errors.labUrl.isError &&
                                                errors.labUrl.message
                                            }
                                        />
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        display="flex"
                                        gap={1.5}
                                        mt={2}
                                    >
                                        <Button
                                            onClick={submitLab}
                                            variant="contained"
                                            color="secondary"
                                            sx={{
                                                fontWeight: 'bold',
                                                textTransform: 'none',
                                                borderRadius: '8px',
                                                px: 4
                                            }}
                                        >
                                            {isEditMode
                                                ? 'Save Lab'
                                                : 'Add Lab'}
                                        </Button>

                                        {isEditMode && (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={handleClickOpen}
                                                sx={{
                                                    minWidth: '56px',
                                                    borderRadius: '8px'
                                                }}
                                            >
                                                <DeleteIcon />
                                            </Button>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        </CardContent>
                    </Card>

                    <Dialogue
                        open={open}
                        handleClose={handleClose}
                        name="Lab"
                        handleDelete={handleDeleteLab}
                    />
                    <ToastContainer />
                </Box>
            )}
        </>
    );
};

export default Lab;
